<script setup>
import { computed } from 'vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useMessageContext } from '../provider.js';

const { content, createdAt } = useMessageContext();

// Aceleres: nas movimentacoes, as acoes automaticas do robo da Aurora (admin real
// cujo token a Aurora usa) saem com o nome do usuario-robo. Esse nome e EXCLUSIVO
// do robo (nenhuma pessoa real), entao o exibimos como "Sistema Aceleres". Como a
// activity nao carrega sender_id, a deteccao e pelo nome embutido no texto. O robo
// ja foi renomeado de "Midiã de Almeida Lacerda Pereira" para "Midiã L." — manter ambos.
const AURORA_ROBOT_NAMES = ['Midiã de Almeida Lacerda Pereira', 'Midiã L.'];
// Aceleres: o user tecnico da integracao Evolution (id 541, "Integração Aceleres")
// nao deve aparecer com esse nome no historico. Renomeado para "Zoraxy". Cobre
// mensagens antigas ja gravadas (o backend novo ja gera "Zoraxy" direto).
const INTEGRATION_NAMES = ['Integração Aceleres', 'Integracao Aceleres'];
const displayContent = computed(() => {
  let text = content.value || '';
  // O nome do robo e EXCLUSIVO dele (nenhuma pessoa real), entao substituimos por
  // "Sistema Aceleres" em QUALQUER movimentacao dele — nao so nas de label de funil.
  AURORA_ROBOT_NAMES.forEach(name => {
    text = text.split(name).join('Sistema Aceleres');
  });
  INTEGRATION_NAMES.forEach(name => {
    text = text.split(name).join('Zoraxy');
  });
  // Fallback: 'added ia'/'adicionou ia' e EXCLUSIVO do robo da Aurora — garante o
  // rotulo mesmo se o admin-robo for renomeado de novo (nome fora da lista acima).
  text = text.replace(
    /^.+?\s+(?=(added|adicionou)\s+ia\b)/i,
    'Sistema Aceleres '
  );
  // remove o prefixo de engrenagem das mensagens do Sistema Aceleres
  return text.replace(/^⚙️\s*/, '');
});

const esc = s =>
  String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Aceleres: estrutura as movimentacoes do "Sistema Aceleres" em card padrao
// (titulo em negrito + descricao com data/hora e nomes em destaque), para
// que todas fiquem alinhadas e com o mesmo formato visual.
const parsed = computed(() => {
  const raw = (displayContent.value || '').replace(/^⚙️\s*/, '');
  const time = messageTimestamp(createdAt.value, 'HH:mm');
  const dt = `${messageTimestamp(createdAt.value, 'dd/MM/yyyy')} às ${time}`;
  let m;

  m = raw.match(/^Sistema Aceleres: conversa atribuída para (.+?)\.?$/i);
  if (m)
    return {
      icon: 'assigned',
      title: 'Atendimento distribuído',
      compact: `Atendimento distribuído para ${m[1]}`,
      html: `Este atendimento foi direcionado automaticamente em <b>${dt}</b> para <b>${esc(m[1])}</b>.`,
    };

  m = raw.match(
    /^Sistema Aceleres: conversa sem responsável — atribuída automaticamente para (.+?)\.?$/i
  );
  if (m)
    return {
      icon: 'assigned',
      title: 'Atendimento distribuído',
      compact: `Atendimento distribuído para ${m[1]}`,
      html: `Este atendimento estava sem responsável e foi direcionado automaticamente em <b>${dt}</b> para <b>${esc(m[1])}</b>.`,
    };

  m = raw.match(
    /^Sistema Aceleres: sem resposta de (.+?) em ([\d.,]+)\s*min — reatribuído para (.+?)\.?$/i
  );
  if (m)
    return {
      icon: 'expired',
      title: 'Atendimento expirado',
      compact: `Atendimento expirado; direcionado para ${m[3]}`,
      html: `O limite de tempo para que <b>${esc(m[1])}</b> iniciasse o atendimento expirou em <b>${dt}</b>, sendo direcionado para <b>${esc(m[3])}</b>.`,
    };

  m = raw.match(
    /^Sistema Aceleres: reatribuído de (.+?) para (.+?)( \(indisponível\))?\.?$/i
  );
  if (m)
    return {
      icon: 'assigned',
      title: 'Atendimento redistribuído',
      compact: `Atendimento redistribuído: ${m[1]} → ${m[2]}`,
      html: `Este atendimento foi redistribuído de <b>${esc(m[1])}</b> para <b>${esc(m[2])}</b> em <b>${dt}</b>${m[3] ? ' — vendedor indisponível' : ''}.`,
    };
  m = raw.match(
    /^Sistema Aceleres: (.+?) respondeu primeiro — lead ancorado com quem atendeu(?: \(estava com (.+?)\))?\.?$/i
  );
  if (m)
    return {
      icon: 'took',
      title: null,
      compact: `${m[1]} assumiu o atendimento`,
      html: `<b>${esc(m[1])}</b> assumiu o atendimento em <b>${dt}</b>${m[2] ? ` (estava com <b>${esc(m[2])}</b>)` : ''}.`,
    };

  // Evento: agente clicou em "Iniciar" e assumiu a conversa
  m = raw.match(/^Aceleres: (.+?) assumiu o atendimento no canal (.+?)\.?$/i);
  if (m)
    return {
      icon: 'took-over',
      title: 'Atendimento assumido',
      compact: `${m[1]} assumiu o atendimento`,
      html: `<b>${esc(m[1])}</b> assumiu o atendimento em <b>${dt}</b> no canal <b>${esc(m[2])}</b>.`,
    };

  // Evento: janela de 24h do WhatsApp/API expirou
  m = raw.match(/^Aceleres: Tempo de sessão excedido em 24h\.?$/i);
  if (m)
    return {
      icon: 'expired',
      title: 'Sessão expirada',
      compact: 'Sessão de 24 h encerrada',
      html: `A janela de atendimento de 24 horas foi encerrada em <b>${dt}</b>. Uma nova mensagem do cliente reabrirá a sessão.`,
    };

  return null;
});

const readableTime = computed(() => messageTimestamp(createdAt.value, 'HH:mm'));

const iconType = computed(() => {
  if (parsed.value) return parsed.value.icon;
  const t = (content.value || '').toLowerCase();
  if (t.includes('expir') || t.includes('limite de tempo')) return 'expired';
  if (
    t.includes('distribuí') ||
    t.includes('direcionado') ||
    t.includes('atribuí') ||
    t.includes('assigned')
  )
    return 'assigned';
  if (t.includes('assumiu') || t.includes('took over')) return 'took-over';
  if (
    t.includes('resolvid') ||
    t.includes('resolved') ||
    t.includes('encerrad')
  )
    return 'resolved';
  if (t.includes('reaberto') || t.includes('reopened')) return 'reopened';
  return 'info';
});

const iconMeta = computed(() => {
  const map = {
    assigned: {
      bg: 'bg-blue-100 dark:bg-blue-900/40',
      color: 'text-blue-600 dark:text-blue-400',
    },
    expired: {
      bg: 'bg-red-100 dark:bg-red-900/40',
      color: 'text-red-600 dark:text-red-400',
    },
    took: {
      bg: 'bg-indigo-100 dark:bg-indigo-900/40',
      color: 'text-indigo-600 dark:text-indigo-400',
    },
    // Aceleres: usuário atribuiu a conversa a si mesmo (botão "Iniciar").
    // Ícone Lucide user-round-check (pessoa + check). (pedido 2026-07-02)
    // NB: chave com hífen precisa de aspas, senão vira 'took - over'.
    'took-over': {
      bg: 'bg-indigo-100 dark:bg-indigo-900/40',
      color: 'text-indigo-600 dark:text-indigo-400',
    },
    resolved: {
      bg: 'bg-green-100 dark:bg-green-900/40',
      color: 'text-green-600 dark:text-green-400',
    },
    reopened: {
      bg: 'bg-orange-100 dark:bg-orange-900/40',
      color: 'text-orange-600 dark:text-orange-400',
    },
    info: {
      bg: 'bg-slate-100 dark:bg-slate-700/60',
      color: 'text-slate-500 dark:text-slate-400',
    },
  };
  return map[iconType.value] || map.info;
});

const compactIcon = computed(() => {
  const map = {
    assigned: 'i-lucide-refresh-cw',
    expired: 'i-lucide-circle-alert',
    took: 'i-lucide-arrow-right',
    'took-over': 'i-lucide-user-round-check',
    resolved: 'i-lucide-circle-check',
    reopened: 'i-lucide-rotate-ccw',
    info: 'i-lucide-info',
  };

  return map[iconType.value] || map.info;
});
</script>

<template>
  <div
    class="aceleres-activity-event flex items-start w-full gap-2 py-1.5 px-3 my-0.5"
    :class="{ 'aceleres-activity-card': parsed }"
  >
    <div
      class="acl-activity-detail flex items-start gap-2.5 min-w-0 max-w-[78%]"
    >
      <!-- Ícone com fundo colorido -->
      <span
        class="flex-shrink-0 flex items-center justify-center rounded-full w-7 h-7 mt-0.5"
        :class="[iconMeta.bg, iconMeta.color]"
      >
        <!-- assigned: setas circulares -->
        <svg
          v-if="iconType === 'assigned'"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2.2"
          width="16"
          height="16"
        >
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path
            d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"
          />
        </svg>
        <!-- expired: alerta círculo -->
        <svg
          v-else-if="iconType === 'expired'"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2.2"
          width="16"
          height="16"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <!-- took: seta direita -->
        <svg
          v-else-if="iconType === 'took'"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2.2"
          width="16"
          height="16"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
        <!-- took-over: usuário assumiu atendimento (Lucide user-round-check) -->
        <svg
          v-else-if="iconType === 'took-over'"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2.2"
          width="16"
          height="16"
        >
          <path d="M2 21a8 8 0 0 1 13.292-6" />
          <circle cx="10" cy="8" r="5" />
          <path d="m16 19l2 2l4-4" />
        </svg>
        <!-- resolved: check círculo -->
        <svg
          v-else-if="iconType === 'resolved'"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2.2"
          width="16"
          height="16"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <!-- reopened: seta circular CCW -->
        <svg
          v-else-if="iconType === 'reopened'"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2.2"
          width="16"
          height="16"
        >
          <polyline points="1 4 1 10 7 10" />
          <path d="M3.51 15a9 9 0 1 0 .49-3.58" />
        </svg>
        <!-- info: i -->
        <svg
          v-else
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2.2"
          width="16"
          height="16"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </span>

      <!-- Movimentação estruturada (Sistema Aceleres): título + descrição -->
      <div v-if="parsed" class="flex flex-col gap-0.5 min-w-0">
        <span
          v-if="parsed.title"
          class="text-[13px] font-semibold leading-5 text-n-slate-12"
        >
          {{ parsed.title }}
        </span>
        <span
          v-dompurify-html="parsed.html"
          v-twemoji
          class="text-xs leading-5 text-n-slate-11 [&_b]:font-semibold [&_b]:text-n-slate-12"
        />
      </div>

      <!-- Demais atividades (formato padrão) -->
      <div v-else class="flex flex-col gap-0.5 min-w-0">
        <span
          v-dompurify-html="displayContent"
          v-twemoji
          class="text-xs text-n-slate-11 leading-4 [&_b]:font-semibold [&_a]:text-blue-400 [&_a]:underline"
        />
        <span class="text-[10px] text-n-slate-9">{{ readableTime }}</span>
      </div>
    </div>
    <div class="acl-activity-mobile">
      <span
        class="acl-activity-mobile__icon"
        :class="[iconMeta.bg, iconMeta.color]"
        aria-hidden="true"
      >
        <span :class="compactIcon" />
      </span>
      <span v-if="parsed" class="acl-activity-mobile__text">
        {{ parsed.compact }}
      </span>
      <span
        v-else
        v-dompurify-html="displayContent"
        v-twemoji
        class="acl-activity-mobile__text"
      />
      <span class="acl-activity-mobile__separator" aria-hidden="true">·</span>
      <span class="acl-activity-mobile__time">{{ readableTime }}</span>
    </div>
  </div>
</template>

<style scoped>
/* Aceleres: classe própria dos eventos de atividade/sistema no chat (ex.:
   "Conversa foi reaberta por Lucas"). Sem as linhas laterais (removidas do
   template) e centralizado no meio da conversa. (pedido 2026-07-01) */
.aceleres-activity-event {
  justify-content: center;
}
/* Aceleres: movimentações estruturadas (card com título + descrição — ex.:
   "Atendimento distribuído", "Sessão expirada", "Atendimento assumido") ficam
   à ESQUERDA; as atividades avulsas (texto simples) continuam centralizadas.
   Classe própria pra desacoplar o alinhamento dos dois formatos — antes o
   .aceleres-activity-event centralizado puxava os cards junto. (pedido 2026-07-02) */
.aceleres-activity-event.aceleres-activity-card {
  justify-content: flex-start;
}

.acl-activity-mobile__text {
  color: #64748b;
}

.acl-activity-mobile__separator {
  flex: 0 0 auto;
  color: #8491a3;
  font-weight: 600;
  line-height: 1;
}

:global(.dark) .acl-activity-mobile__text {
  color: #64748b;
}

@media (min-width: 769px) {
  .acl-activity-detail {
    padding: 7px 11px;
    border: 1px solid rgb(15 23 42 / 7%);
    border-radius: 11px;
    background: rgb(255 255 255 / 86%);
    box-shadow: 0 1px 2px rgb(15 23 42 / 8%);
    backdrop-filter: blur(4px);
  }

  .aceleres-activity-event .acl-activity-detail > div > span {
    color: #475569;
    font-weight: 600;
  }

  .aceleres-activity-card .acl-activity-detail > div > span:first-child,
  .aceleres-activity-event .acl-activity-detail b {
    color: #1f2937;
    font-weight: 700;
  }

  .aceleres-activity-event:not(.aceleres-activity-card)
    .acl-activity-detail
    > div
    > span:last-child {
    color: #64748b;
    font-weight: 600;
  }
}
</style>
